import React, { useCallback, useEffect, useState } from "react";
import axios from "../../api";
import {
  Container,
  Paper,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Pagination,
} from "@mui/material";
import { GppMaybe, Download, Refresh } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AdminLayout from "../AdminLayout";

const PER_PAGE = 15;

function WarrantyReport({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(30);
  const [includeExpired, setIncludeExpired] = useState("1");
  const [page, setPage] = useState(1);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/assets/reports/warranty", {
        params: {
          days,
          includeExpired: includeExpired === "1",
        },
      });
      setRows(res.data.result || []);
      setPage(1);
    } catch (err) {
      toast.error("Failed to load warranty report.");
    } finally {
      setLoading(false);
    }
  }, [days, includeExpired]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleLogout = () => {
    if (onLogout) return onLogout();
    localStorage.clear();
    navigate("/login");
  };

  const pageCount = Math.ceil(rows.length / PER_PAGE) || 1;
  const safePage = Math.min(page, pageCount);
  const paged = rows.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const exportCsv = () => {
    if (!rows.length) {
      toast.info("Nothing to export.");
      return;
    }
    const cols = [
      "assetTag",
      "serialNumber",
      "model",
      "status",
      "assignedEmail",
      "warrantyExpiry",
      "warrantyStatus",
    ];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.join(",")];
    rows.forEach((a) => {
      lines.push(
        [
          a.assetTag,
          a.serialNumber,
          a.model,
          a.status,
          a.assignedTo?.email,
          a.warrantyExpiry
            ? new Date(a.warrantyExpiry).toISOString().slice(0, 10)
            : "",
          a.warrantyStatus,
        ]
          .map(escape)
          .join(",")
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `warranty-report-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} asset(s)`);
  };

  const statusColor = (s) =>
    s === "Expired" ? "error" : s === "ExpiringSoon" ? "warning" : "default";

  return (
    <AdminLayout
      title="Warranty Report"
      subtitle="Assets expired or nearing warranty end"
      onLogout={handleLogout}
    >
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <GppMaybe color="warning" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Warranty watchlist
            </Typography>
            <Chip size="small" color="warning" label={rows.length} />
            <Box sx={{ flexGrow: 1 }} />
            <TextField
              size="small"
              select
              label="Window"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value={30}>30 days</MenuItem>
              <MenuItem value={60}>60 days</MenuItem>
              <MenuItem value={90}>90 days</MenuItem>
            </TextField>
            <TextField
              size="small"
              select
              label="Expired"
              value={includeExpired}
              onChange={(e) => setIncludeExpired(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="1">Include expired</MenuItem>
              <MenuItem value="0">Expiring only</MenuItem>
            </TextField>
            <Button
              variant="text"
              startIcon={<Download />}
              onClick={exportCsv}
              disabled={!rows.length}
            >
              Export
            </Button>
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={fetchReport}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No assets in this warranty window.
            </Typography>
          ) : (
            <>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Asset</TableCell>
                      <TableCell>Serial</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Assigned to</TableCell>
                      <TableCell>Warranty</TableCell>
                      <TableCell>Expiry</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map((a) => (
                      <TableRow key={a._id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {a.model || a.assetTag || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.assetTag}
                          </Typography>
                        </TableCell>
                        <TableCell>{a.serialNumber || "—"}</TableCell>
                        <TableCell>{a.status}</TableCell>
                        <TableCell>
                          {a.assignedTo?.email ? (
                            <>
                              <Typography variant="body2">
                                {a.assignedTo?.empName || "—"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {a.assignedTo.email}
                              </Typography>
                            </>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={a.warrantyStatus || "—"}
                            color={statusColor(a.warrantyStatus)}
                          />
                        </TableCell>
                        <TableCell>
                          {a.warrantyExpiry
                            ? new Date(a.warrantyExpiry).toLocaleDateString()
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={pageCount}
                  page={safePage}
                  onChange={(e, v) => setPage(v)}
                />
              </Box>
            </>
          )}
        </Paper>
      </Container>
      <ToastContainer position="top-right" autoClose={2500} />
    </AdminLayout>
  );
}

export default WarrantyReport;
